import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Loader2, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { API_BASE } from '../../config/api';

const URL_BASE = `${API_BASE}/api/admin/external-courses`;

// CSV / Excel upload: preview (dry run) first, then import the valid rows
export default function ExternalCourseBulkUpload({ open, token, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const reset = () => { setFile(null); setPreview(null); };
  const close = () => { reset(); onClose(); };

  const send = async (dryRun) => {
    const fd = new FormData();
    fd.append('file', file);
    setBusy(true);
    try {
      const res = await axios.post(`${URL_BASE}/bulk-upload?dry_run=${dryRun}`, fd, { headers });
      if (dryRun) setPreview(res.data);
      else {
        toast.success(`Imported: ${res.data.created} added, ${res.data.updated} updated`);
        reset();
        onDone();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const downloadCsv = async (path, name) => {
    const res = await axios.get(`${URL_BASE}${path}`, { headers, responseType: 'blob' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(res.data);
    a.download = name;
    a.click();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="bg-navigate-surface border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ext-bulk-dialog">
        <DialogHeader><DialogTitle className="text-white">Bulk upload external courses</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-400">
          Upload a CSV or Excel file. Use <b>Export all courses</b> to get every course with its <code>course_id</code>, edit it, and upload it again to update.
          Rows with an existing <code>course_id</code> (or an existing course link) update that course, and blank cells leave the value unchanged;
          other rows add new courses. Required for new courses: <b>title</b> and an <b>https://</b> course link.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" onClick={() => downloadCsv('-template', 'external_courses_template.csv')} className="border-zinc-700" data-testid="ext-bulk-template">
            <Download className="h-4 w-4 mr-2" /> Download template
          </Button>
          <Button variant="outline" onClick={() => downloadCsv('-export', 'external_courses.csv')} className="border-zinc-700" data-testid="ext-bulk-export">
            <Download className="h-4 w-4 mr-2" /> Export all courses
          </Button>
          <label className="inline-flex items-center gap-2 px-3 h-9 rounded-md border border-zinc-700 text-sm text-zinc-200 cursor-pointer hover:border-navigate-gold" data-testid="ext-bulk-file-label">
            <Upload className="h-4 w-4" />
            <span className="max-w-[180px] truncate">{file ? file.name : 'Choose file'}</span>
            <input type="file" accept=".csv,.xlsx" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
              className="hidden" data-testid="ext-bulk-file" />
          </label>
          <Button onClick={() => send(true)} disabled={!file || busy} variant="outline" className="border-zinc-700" data-testid="ext-bulk-preview">
            {busy && !preview ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Check file
          </Button>
        </div>
        {preview && (
          <div className="space-y-2" data-testid="ext-bulk-results">
            <p className="text-sm text-zinc-300" data-testid="ext-bulk-summary">
              {preview.total} rows: <span className="text-green-400">{preview.valid} ready</span>, <span className="text-red-400">{preview.invalid} with errors</span>
            </p>
            <div className="max-h-64 overflow-y-auto border border-zinc-800 rounded-lg">
              <table className="w-full text-sm">
                <thead className="text-zinc-500 text-left"><tr><th className="p-2">Row</th><th className="p-2">Title</th><th className="p-2">Action</th><th className="p-2">Problems</th></tr></thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.row} className="border-t border-zinc-800" data-testid={`ext-bulk-row-${r.row}`}>
                      <td className="p-2 text-zinc-500">{r.row}</td>
                      <td className="p-2 text-white">{r.title}</td>
                      <td className="p-2 text-zinc-300">{r.action === 'update' ? 'Update' : 'Add new'}</td>
                      <td className="p-2 text-red-400">{r.errors.join('; ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={close} className="border-zinc-700" data-testid="ext-bulk-cancel">Cancel</Button>
          <Button onClick={() => send(false)} disabled={!preview || !preview.valid || busy} className="btn-gold" data-testid="ext-bulk-import">
            {busy && preview && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Import {preview?.valid || 0} valid rows
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
